/**
 * Shop the look.
 *
 * Handles the mobile overlay and the hotspot dots, which scroll the product
 * list to the matching card.
 *
 * On mobile the product list opens as a small overlay in the middle of the
 * screen. While open it is moved to the end of the page, so nothing else on
 * the page, such as the sticky add to cart bar, can sit on top of it, and it
 * is put back in place when it closes.
 */
class ShopTheLook extends HTMLElement {
  #controller = new AbortController();
  #placeholder = null;

  connectedCallback() {
    const { signal } = this.#controller;

    this.aside = this.querySelector('.shop-the-look__aside');
    this.panel = this.querySelector('[data-shop-the-look-panel]');
    this.openButton = this.querySelector('[data-shop-the-look-open]');
    this.closeButton = this.querySelector('[data-shop-the-look-close]');
    this.underlay = this.querySelector('[data-shop-the-look-underlay]');
    this.dots = Array.from(this.querySelectorAll('[data-shop-the-look-dot]'));
    this.products = Array.from(this.querySelectorAll('[data-shop-the-look-product]'));

    this.openButton?.addEventListener('click', () => this.open(), { signal });
    this.closeButton?.addEventListener('click', () => this.close(), { signal });
    this.underlay?.addEventListener('click', () => this.close(), { signal });

    for (const dot of this.dots) {
      dot.addEventListener('click', this.#handleDotClick, { signal });
    }

    document.addEventListener('keydown', this.#handleKeyDown, { signal });
    window.matchMedia('(max-width: 749px)').addEventListener(
      'change',
      (event) => {
        if (!event.matches) this.close();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.close();
    this.#controller.abort();
  }

  get #isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  open() {
    if (!this.aside) return;

    if (this.#isMobile && !this.#placeholder) {
      this.#placeholder = document.createComment('shop-the-look');
      this.aside.before(this.#placeholder);
      document.body.appendChild(this.aside);
      // Let the browser place it before fading it in.
      void this.aside.offsetWidth;
    }

    this.classList.add('is-open');
    this.aside.classList.add('is-open');
    if (this.#isMobile) document.body.classList.add('shop-the-look-open');
    this.closeButton?.focus();
  }

  close() {
    if (!this.aside) return;
    const wasOpen = this.isOpen;

    this.classList.remove('is-open');
    this.aside.classList.remove('is-open');
    document.body.classList.remove('shop-the-look-open');

    // Put it back in place once the fade out has finished, unless it has
    // been opened again in the meantime.
    if (this.#placeholder) {
      setTimeout(() => {
        if (this.isOpen || !this.#placeholder) return;
        if (this.#placeholder.isConnected) this.#placeholder.replaceWith(this.aside);
        this.#placeholder = null;
      }, 350);
    }

    if (wasOpen) this.openButton?.focus();
  }

  #handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.isOpen) this.close();
  };

  #handleDotClick = (event) => {
    const index = Number(event.currentTarget.dataset.shopTheLookDot);
    const product = this.products[index];

    if (!product) return;

    for (const dot of this.dots) {
      dot.classList.toggle('is-selected', dot === event.currentTarget);
    }

    if (this.#isMobile) this.open();

    requestAnimationFrame(() => {
      product.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });
  };
}

if (!customElements.get('shop-the-look')) {
  customElements.define('shop-the-look', ShopTheLook);
}
